// // ProductGroupSelect.jsx
// import React, { useMemo } from 'react';
// import PropTypes from 'prop-types';

// export default function ProductGroupSelect({
//     itemGroups = [],
//     value = '',
//     onChange = () => { },
//     onAddNew = () => { },
//     placeholder = '— Select product group —'
// }) {
//     // Build grouped structure
//     const grouped = useMemo(() => {
//         const parents = new Map();
//         const children = new Map();
//         const order = [];

//         itemGroups.forEach(g => {
//             if (Number(g.IsPrimary) === 1) {
//                 const heading = (g.GroupName ?? '').toString().trim();
//                 if (!heading) return;
//                 parents.set(heading, g);
//                 if (!order.includes(heading)) order.push(heading);
//             }
//         });

//         itemGroups.forEach(g => {
//             if (Number(g.IsPrimary) !== 1) {
//                 const parentId = String(g.UnderGRP);
//                 const parentGroup = Array.from(parents.values()).find(pg => String(pg.ProdGRPID) === parentId);
//                 if (!parentGroup) return;
//                 const heading = (parentGroup.GroupName ?? '').toString().trim();
//                 if (!heading) return;
//                 if (!children.has(heading)) children.set(heading, []);
//                 const already = children.get(heading).some(ch => String(ch.ProdGRPID) === String(g.ProdGRPID));
//                 if (!already) children.get(heading).push(g);
//                 if (!order.includes(heading)) order.push(heading);
//             }
//         });

//         return order.map(heading => ({
//             heading,
//             parent: parents.get(heading),
//             children: children.get(heading) ?? []
//         }));
//     }, [itemGroups]);

//     const selectValue = value ? String(value) : '';

//     const handleChange = (e) => {
//         const val = e.target.value;

//         if (val === '__add__') {
//             onAddNew();
//             return;
//         }

//         if (val === '') {
//             onChange('', null);
//             return;
//         }

//         const selected = itemGroups.find(g => String(g.ProdGRPID) === val);
//         onChange(val, selected?.GroupName ?? null);
//     };

//     return (
//         <select
//             value={selectValue}
//             onChange={handleChange}
//             style={{
//                 width: '100%',
//                 padding: '6px',
//                 border: '1px solid #ccc',
//                 borderRadius: '4px',
//                 fontSize: '14px',
//                 height: '40px'
//             }}
//         >
//             {/* Placeholder */}
//             <option value="" disabled>
//                 {placeholder}
//             </option>

//             {/* Render grouped items */}
//             {grouped.map(({ heading, children }) => (
//                 <optgroup key={heading} label={heading}>
//                     {children.map(c => (
//                         <option key={c.ProdGRPID} value={String(c.ProdGRPID)}>
//                             {c.GroupName}
//                         </option>
//                     ))}
//                 </optgroup>
//             ))}

//             {/* Add new group */}
//             <option value="__add__">+ Add New Group</option>
//         </select>
//     );
// }

// ProductGroupSelect.propTypes = {
//     itemGroups: PropTypes.array,
//     value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
//     onChange: PropTypes.func,
//     onAddNew: PropTypes.func,
//     placeholder: PropTypes.string
// };






import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    MenuItem,
    Select,
    FormControl,
    ListSubheader,
    InputAdornment,
    TextField,
    Box,
    Typography,
    Divider,
    CircularProgress,
    InputLabel
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    FolderOpen as FolderOpenIcon,
    Category as CategoryIcon,
    Close as CloseIcon
} from '@mui/icons-material';

export default function ProductGroupSelect({
    itemGroups = [],
    value = '',
    onChange = () => { },
    onAddNew = () => { },
    placeholder = '— Select product group —',
    disabled = false,
    loading = false,
    required = false
}) {
    const [searchQuery, setSearchQuery] = useState('');
    const [open, setOpen] = useState(false);

        const requiredAsteriskStyle = {
  '& .MuiFormLabel-asterisk': {
    color: 'red',
  },
};

    // Build grouped structure - FIXED: Only secondary items, group by parent
    const grouped = useMemo(() => {
        const parentMap = new Map();

        // First pass: Create parent map from primary items
        itemGroups.forEach(g => {
            if (Number(g.IsPrimary) === 1) {
                const parentId = String(g.ProdGRPID);
                parentMap.set(parentId, {
                    parent: g,
                    children: []
                });
            }
        });

        // Second pass: Add secondary items to their parent
        itemGroups.forEach(g => {
            if (Number(g.IsPrimary) !== 1) {
                const parentId = String(g.UnderGRP);
                if (parentMap.has(parentId)) {
                    parentMap.get(parentId).children.push(g);
                }
            }
        });

        // Convert to array
        return Array.from(parentMap.values());
    }, [itemGroups]);

    // Filter based on search query - only filters children, not headers
    const filteredGrouped = useMemo(() => {
        if (!searchQuery.trim()) return grouped;

        const query = searchQuery.toLowerCase();
        return grouped
            .map(group => ({
                ...group,
                children: group.children.filter(c =>
                    c.GroupName.toLowerCase().includes(query)
                )
            }))
            .filter(group => group.children.length > 0); // Only show groups with matching children
    }, [grouped, searchQuery]);

    const selectValue = value ? String(value) : '';
    const selectedGroup = itemGroups.find(g => String(g.ProdGRPID) === selectValue);

    const handleChange = useCallback((e) => {
        const val = e.target.value;

        if (val === '__add__') {
            onAddNew();
            setOpen(false);
            return;
        }

        if (val === '') {
            onChange('', null);
            setOpen(false);
            return;
        }

        const selected = itemGroups.find(g => String(g.ProdGRPID) === val);
        onChange(val, selected?.GroupName ?? null);
        setOpen(false);
    }, [itemGroups, onChange]);

    const handleSearchChange = useCallback((e) => {
        setSearchQuery(e.target.value);
    }, []);

    return (
        <FormControl fullWidth size="small" disabled={disabled || loading} required={true} >
            <InputLabel required={required} shrink sx={requiredAsteriskStyle}>
                Product Group
            </InputLabel>
            <Select
                value={selectValue}
                onChange={handleChange}
                displayEmpty
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => {
                    setOpen(false);
                    setSearchQuery('');
                }}
                renderValue={(selected) => {
                    if (!selected || selected === '') {
                        return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CategoryIcon sx={{ fontSize: '1.2rem', color: '#999' }} />
                                <Typography sx={{ color: '#999', fontSize: '0.95rem' }}>
                                    {placeholder}
                                </Typography>
                            </Box>
                        );
                    }
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {selectedGroup?.IsPrimary === 1 ? (
                                <FolderOpenIcon sx={{ fontSize: '1.2rem', color: '#ff9800' }} />
                            ) : (
                                <CategoryIcon sx={{ fontSize: '1.2rem', color: '#2196f3' }} />
                            )}
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                                {selectedGroup?.GroupName || placeholder}
                            </Typography>
                        </Box>
                    );
                }}
                sx={{
                    height: 38,
                    // borderRadius: '8px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#ddd',
                        transition: 'border-color 0.3s'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2196f3',
                        boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#1976d2',
                        borderWidth: '2px',
                        boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.1)'
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                        cursor: 'not-allowed'
                    },
                    '& .MuiSelect-icon': {
                        transition: 'transform 0.3s'
                    },
                    '&.Mui-focused .MuiSelect-icon': {
                        transform: 'rotate(180deg)'
                    }
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            borderRadius: '8px',
                            marginTop: '8px',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                            '&::-webkit-scrollbar': {
                                width: '8px'
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: 'transparent'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#bbb',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: '#999'
                                }
                            },
                            maxHeight: '400px'
                        }
                    },
                    MenuListProps: {
                        sx: {
                            py: 1,
                            '& .MuiMenuItem-root': {
                                transition: 'all 0.2s'
                            }
                        }
                    }
                }}
            >
                {/* Placeholder */}
                <MenuItem value="" disabled sx={{ display: 'none' }}>
                    {placeholder}
                </MenuItem>

                {/* Search Box */}
                <Box
                    sx={{
                        p: 1.5,
                        display: 'flex',
                        gap: 1,
                        position: 'sticky',
                        top: 0,
                        backgroundColor: '#fff',
                        borderBottom: '1px solid #eee',
                        zIndex: 1000
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <TextField
                        required={required}
                        autoFocus
                        size="small"
                        placeholder="Search groups..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        fullWidth
                        onKeyDown={(e) => e.stopPropagation()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: '1.2rem', color: '#999' }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery && (
                                <InputAdornment position="end">
                                    <CloseIcon
                                        sx={{
                                            fontSize: '1.2rem',
                                            color: '#999',
                                            cursor: 'pointer',
                                            '&:hover': { color: '#333' }
                                        }}
                                        onClick={() => setSearchQuery('')}
                                    />
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: '36px',
                                borderRadius: '6px',
                                backgroundColor: '#f8f8f8'
                            }
                        }}
                    />
                </Box>

                {/* Loading state */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}

                {/* No results */}
                {!loading && filteredGrouped.length === 0 && searchQuery.trim() && (
                    <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}>
                        <Typography sx={{ color: '#999', fontSize: '0.9rem' }}>
                            No groups found
                        </Typography>
                    </MenuItem>
                )}

                {/* No groups at all */}
                {!loading && grouped.length === 0 && !searchQuery.trim() && (
                    <MenuItem disabled sx={{ justifyContent: 'center', py: 2 }}>
                        <Typography sx={{ color: '#999', fontSize: '0.9rem' }}>
                            No groups available
                        </Typography>
                    </MenuItem>
                )}

                {/* Render grouped items - Parents NOW SELECTABLE */}
                {!loading && filteredGrouped.length > 0 && filteredGrouped.map(({ parent, children }) => [
                    // Primary Item - NOW SELECTABLE as MenuItem
                    <MenuItem
                        key={`parent-${parent.ProdGRPID}`}
                        value={String(parent.ProdGRPID)}
                        sx={{
                            paddingLeft: '16px',
                            fontSize: '0.9rem',
                            fontWeight: 700,
                            color: '#333',
                            backgroundColor: 'linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%)',
                            borderBottom: '1px solid #eee',
                            marginTop: '4px',
                            transition: 'all 0.2s',
                            position: 'relative',
                            '&:hover': {
                                backgroundColor: '#e3f2fd',
                                pl: '24px',
                                color: '#1976d2',
                                fontWeight: 600
                            },
                            '&.Mui-selected': {
                                backgroundColor: '#e3f2fd',
                                color: '#0d47a1',
                                fontWeight: 700
                            }
                        }}
                    >
                        <FolderOpenIcon sx={{ mr: 1, fontSize: '1rem', color: '#ff9800' }} />
                        {parent.GroupName}
                    </MenuItem>,

                    // Child Items - Selectable with indentation
                    ...children.map(c => (
                        <MenuItem
                            key={`child-${c.ProdGRPID}`}
                            value={String(c.ProdGRPID)}
                            sx={{
                                paddingLeft: '56px',
                                fontSize: '0.9rem',
                                color: '#666',
                                transition: 'all 0.2s',
                                position: 'relative',
                                '&::before': {
                                    content: '"└"',
                                    position: 'absolute',
                                    left: '40px',
                                    color: '#ddd',
                                    fontWeight: 'bold'
                                },
                                '&:hover': {
                                    backgroundColor: '#e3f2fd',
                                    pl: '64px',
                                    color: '#1976d2',
                                    fontWeight: 500
                                },
                                '&.Mui-selected': {
                                    backgroundColor: '#e3f2fd',
                                    color: '#0d47a1',
                                    fontWeight: 600
                                }
                            }}
                        >
                            {c.GroupName}
                        </MenuItem>
                    ))
                ])}

                {/* Divider */}
                {!loading && filteredGrouped.length > 0 && (
                    <Divider sx={{ my: 0.5, borderColor: '#f0f0f0' }} />
                )}

                {/* Add new group */}
                {!loading && (
                    <MenuItem
                        value="__add__"
                        sx={{
                            paddingLeft: '16px',
                            color: '#1976d2',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: '#e3f2fd',
                                pl: '24px'
                            }
                        }}
                    >
                        <AddIcon sx={{ mr: 1.5, fontSize: '1.2rem' }} />
                        Add New Group
                    </MenuItem>
                )}
            </Select>
        </FormControl>
    );
}

ProductGroupSelect.propTypes = {
    itemGroups: PropTypes.array,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    onAddNew: PropTypes.func,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    loading: PropTypes.bool
};